import { useEffect, useState, useCallback, useMemo } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Search,
  NotebookPen,
  Video,
  Star,
  Crosshair,
  Shield,
  Trophy,
  ChevronDown,
  ChevronUp,
  Pencil,
} from "lucide-react";
import MatchReportDialog from "../components/convocations/MatchReportDialog";

function ResultPill({ us, them }) {
  if (us == null || them == null) return null;
  const won = us > them;
  const drew = us === them;
  const cls = won
    ? "bg-emerald-100 text-emerald-700"
    : drew
    ? "bg-amber-100 text-amber-700"
    : "bg-red-100 text-red-700";
  return (
    <span className={`px-2.5 py-1 rounded-full text-sm font-bold tabular-nums ${cls}`}>
      {us}–{them}
    </span>
  );
}

function ReportCard({ c, players, expanded, onToggle, onEdit, canEdit }) {
  const mvp = c.mvp_player_id ? players.find((p) => p.id === c.mvp_player_id) : null;
  const hasContent =
    c.report_summary ||
    c.tactical_defense ||
    c.tactical_midfield ||
    c.tactical_attack ||
    c.mvp_player_id ||
    (c.salient_events || []).length > 0 ||
    c.veo_video_url;

  return (
    <Card className="border-slate-200" data-testid={`analysis-${c.id}`}>
      <button
        onClick={onToggle}
        className="w-full p-5 flex items-center gap-3 flex-wrap text-left hover:bg-slate-50/60 transition rounded-xl"
      >
        <div className="h-12 w-12 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center shrink-0">
          <NotebookPen size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold">{c.title}</div>
          <div className="text-sm text-slate-500 mt-0.5">
            {c.match_date}
            {c.opponent && ` · vs ${c.opponent}`}
          </div>
        </div>
        <ResultPill us={c.final_score_us} them={c.final_score_them} />
        {c.veo_video_url && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-violet-50 text-violet-700 text-xs font-semibold">
            <Video size={12} /> Veo
          </span>
        )}
        {mvp && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
            <Star size={12} /> {mvp.last_name}
          </span>
        )}
        {!hasContent && (
          <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-500 text-xs">
            Nessuna analisi
          </span>
        )}
        {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>
      {expanded && (
        <div className="px-5 pb-5 pt-1 border-t border-slate-100 space-y-4">
          {c.report_summary && (
            <div>
              <div className="label-eyebrow mb-1">Commento generale</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.report_summary}</p>
            </div>
          )}
          {(c.tactical_defense || c.tactical_midfield || c.tactical_attack) && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Shield size={14} className="text-sky-500" />
                <div className="label-eyebrow">Valutazione per reparto</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {c.tactical_defense && (
                  <div className="p-3 rounded-lg bg-sky-50/60 border border-sky-100">
                    <div className="text-xs font-semibold text-sky-700 mb-1">Difesa</div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.tactical_defense}</p>
                  </div>
                )}
                {c.tactical_midfield && (
                  <div className="p-3 rounded-lg bg-violet-50/60 border border-violet-100">
                    <div className="text-xs font-semibold text-violet-700 mb-1">Centrocampo</div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.tactical_midfield}</p>
                  </div>
                )}
                {c.tactical_attack && (
                  <div className="p-3 rounded-lg bg-emerald-50/60 border border-emerald-100">
                    <div className="text-xs font-semibold text-emerald-700 mb-1">Attacco</div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.tactical_attack}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          {mvp && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
              <Star size={18} className="text-amber-500" />
              <div>
                <div className="text-xs text-amber-700">MVP della partita</div>
                <div className="font-semibold">
                  #{mvp.jersey_number ?? "—"} {mvp.last_name} {mvp.first_name}
                </div>
              </div>
            </div>
          )}
          {(c.salient_events || []).length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Crosshair size={14} className="text-violet-500" />
                <div className="label-eyebrow">Eventi salienti</div>
              </div>
              <ul className="space-y-1 text-sm">
                {c.salient_events.map((ev) => (
                  <li key={ev.id || `${ev.minute}-${ev.text}`} className="flex gap-2">
                    <span className="font-mono text-slate-400 w-10 shrink-0 text-right">
                      {ev.minute ? `${ev.minute}'` : "—"}
                    </span>
                    <span className="text-slate-700">{ev.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {c.veo_video_url && (
            <a
              href={c.veo_video_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100 text-sm font-medium"
            >
              <Video size={14} /> Apri video Veo Cam
            </a>
          )}
          {c.veo_notes && (
            <div>
              <div className="label-eyebrow mb-1">Spunti dal video</div>
              <p className="text-sm text-slate-600 whitespace-pre-wrap italic">{c.veo_notes}</p>
            </div>
          )}
          {canEdit && (
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={() => onEdit(c)} data-testid={`edit-analysis-${c.id}`}>
                <Pencil size={14} className="mr-1" /> Modifica analisi
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function AnalysisPage() {
  const { activeTeam, canEdit, season } = useTeams();
  const [items, setItems] = useState([]);
  const [players, setPlayers] = useState([]);
  const [search, setSearch] = useState("");
  const [resultFilter, setResultFilter] = useState("__all__"); // all | win | draw | loss | played
  const [expandedId, setExpandedId] = useState(null);
  const [editingConv, setEditingConv] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);

  const load = useCallback(async () => {
    if (!activeTeam) return;
    const [c, p] = await Promise.all([
      api.get(`/teams/${activeTeam.id}/convocations`, { params: { season } }),
      api.get(`/teams/${activeTeam.id}/players`),
    ]);
    setItems((c.data || []).sort((a, b) => (b.match_date || "").localeCompare(a.match_date || "")));
    setPlayers(p.data || []);
  }, [activeTeam, season]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    return items.filter((c) => {
      const text = `${c.title || ""} ${c.opponent || ""} ${c.report_summary || ""}`.toLowerCase();
      if (search && !text.includes(search.toLowerCase())) return false;
      const us = c.final_score_us;
      const them = c.final_score_them;
      const played = us != null && them != null;
      if (resultFilter === "played" && !played) return false;
      if (resultFilter === "win" && !(played && us > them)) return false;
      if (resultFilter === "draw" && !(played && us === them)) return false;
      if (resultFilter === "loss" && !(played && us < them)) return false;
      return true;
    });
  }, [items, search, resultFilter]);

  const totalPlayed = items.filter((c) => c.final_score_us != null).length;
  const wins = items.filter((c) => c.final_score_us > c.final_score_them).length;

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  const handleEdit = (c) => {
    setEditingConv(c);
    setReportOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="analysis-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Analisi post-match</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">
            Report partite
          </h1>
          <p className="text-slate-500">
            {totalPlayed} partite giocate · {wins} vittorie · {items.length - totalPlayed} ancora da giocare
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cerca partita o avversario..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-64"
              data-testid="analysis-search"
            />
          </div>
          <Select value={resultFilter} onValueChange={setResultFilter}>
            <SelectTrigger className="w-44" data-testid="analysis-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte</SelectItem>
              <SelectItem value="played">Solo giocate</SelectItem>
              <SelectItem value="win">Vittorie</SelectItem>
              <SelectItem value="draw">Pareggi</SelectItem>
              <SelectItem value="loss">Sconfitte</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map((c) => (
          <ReportCard
            key={c.id}
            c={c}
            players={players}
            expanded={expandedId === c.id}
            onToggle={() => setExpandedId((id) => (id === c.id ? null : c.id))}
            onEdit={handleEdit}
            canEdit={canEdit}
          />
        ))}
        {filtered.length === 0 && (
          <Card className="p-10 text-center text-slate-500 border-dashed">
            <Trophy size={28} className="mx-auto text-slate-300 mb-2" />
            {items.length === 0
              ? "Nessuna convocazione ancora. Crea la prima per iniziare ad analizzare le partite."
              : "Nessun report corrisponde ai filtri."}
          </Card>
        )}
      </div>

      <MatchReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        convocation={editingConv}
        players={players}
        onSaved={load}
      />
    </div>
  );
}
